import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMoney } from "../../hooks/useMoney";
import { useLobRegion } from "../../hooks/useLobRegion";
import axios from "axios";
import { Download, X, ChevronLeft, ChevronRight, Calendar, ChevronDown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import type { RootState } from "../../store/store";
import {
  fetchProcessorSummary,
  fetchProgramSummary,
  fetchPrincipalSummary,
} from "../../features/billing/BillingMISSlice";
import type {
  ProcessorRow,
  ProgramRow,
  PrincipalRow,
  VoucherDetailRow,
  VoucherDetailResult,
} from "./types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActiveTab = "payables" | "receivables";
type PayablesViewMode = "processor" | "program" | "principal";
type ReceivablesViewMode = "principal" | "program";

type PayablesSelectedEntity =
  | { kind: "processor"; label: string; processorPrefix: string }
  | { kind: "program";   label: string; programId: number;   processor: string }
  | { kind: "principal"; label: string; principalId: number; processor: string };

type ReceivablesSelectedEntity =
  | { kind: "principal"; label: string; principalId: number; processor: string }
  | { kind: "program";   label: string; programId: number; principalId: number; processor: string };

interface RevenuePrincipalRow {
  principalId: number;
  principalName: string | null;
  processor: string;
  billingMonth: string;
  issuanceCount: number;
  issuanceAmount: number;
  issuanceFee: number;
  issuanceGst: number;
  issuanceTotal: number;
  redemptionCount: number;
  redemptionFee: number;
  redemptionGst: number;
  redemptionTotal: number;
  grandFee: number;
  grandGst: number;
  grandTotal: number;
}

interface RevenueProgramRow {
  programId: number;
  programName: string | null;
  principalId: number;
  principalName: string | null;
  processor: string;
  billingMonth: string;
  issuanceCount: number;
  issuanceAmount: number;
  issuanceFee: number;
  issuanceGst: number;
  issuanceTotal: number;
  redemptionCount: number;
  redemptionFee: number;
  redemptionGst: number;
  redemptionTotal: number;
  grandFee: number;
  grandGst: number;
  grandTotal: number;
}

interface SummaryResult<T> {
  billingMonth: string;
  total: number;
  data: T[];
}

type ApiBase = "billing-mis" | "revenue-mis";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtMonth = (ym: string) => {
  const [y, m] = ym.split("-");
  return `${MONTHS[parseInt(m) - 1]} ${y}`;
};

const useBillingFormat = () => {
  const { format, locale } = useMoney();
  const fmt = (n: number | null | undefined) =>
    format(n, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—";
  return { fmt, fmtDate };
};

const makeApiClient = (base: ApiBase) =>
  axios.create({
    baseURL: `${window.env.API_URL}/v1/programs/${base}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      "Content-Type": "application/json",
    },
    timeout: 30000,
  });

function sumRows<T extends {
  issuanceCount: number; issuanceAmount: number; issuanceFee: number; issuanceGst: number;
  redemptionCount: number; redemptionFee: number; redemptionGst: number; grandTotal: number;
}>(rows: T[]) {
  return rows.reduce(
    (acc, r) => ({
      issuanceCount:   acc.issuanceCount   + (r.issuanceCount   ?? 0),
      issuanceAmount:  acc.issuanceAmount  + (r.issuanceAmount  ?? 0),
      issuanceFee:     acc.issuanceFee     + (r.issuanceFee     ?? 0),
      issuanceGst:     acc.issuanceGst     + (r.issuanceGst     ?? 0),
      redemptionCount: acc.redemptionCount + (r.redemptionCount ?? 0),
      redemptionFee:   acc.redemptionFee   + (r.redemptionFee   ?? 0),
      redemptionGst:   acc.redemptionGst   + (r.redemptionGst   ?? 0),
      grandTotal:      acc.grandTotal      + (r.grandTotal      ?? 0),
    }),
    { issuanceCount: 0, issuanceAmount: 0, issuanceFee: 0, issuanceGst: 0, redemptionCount: 0, redemptionFee: 0, redemptionGst: 0, grandTotal: 0 }
  );
}

// ---------------------------------------------------------------------------
// MonthPicker
// ---------------------------------------------------------------------------

function MonthPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => parseInt(value.split("-")[0]));
  const ref = useRef<HTMLDivElement>(null);
  const now = new Date();
  const currentYear  = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const selectedYear  = parseInt(value.split("-")[0]);
  const selectedMonth = parseInt(value.split("-")[1]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (m: number) => {
    onChange(`${pickerYear}-${String(m).padStart(2, "0")}`);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setPickerYear(selectedYear); setOpen((o) => !o); }}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-all"
      >
        <Calendar className="h-4 w-4 text-gray-400" />
        <span>{fmtMonth(value)}</span>
        <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black ring-opacity-5">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <button onClick={() => setPickerYear((y) => y - 1)} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-gray-800">{pickerYear}</span>
            <button
              onClick={() => setPickerYear((y) => Math.min(y + 1, currentYear))}
              disabled={pickerYear >= currentYear}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1 p-3">
            {MONTHS.map((label, i) => {
              const m = i + 1;
              const isFuture   = pickerYear === currentYear && m > currentMonth;
              const isSelected = pickerYear === selectedYear && m === selectedMonth;
              return (
                <button key={label} disabled={isFuture} onClick={() => select(m)}
                  className={`rounded-lg py-2 text-sm font-medium transition-colors ${
                    isSelected ? "bg-brand text-white shadow-sm"
                    : isFuture ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                  }`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ViewModeSelect
// ---------------------------------------------------------------------------

function ViewModeSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-all"
      >
        <span>{current?.label ?? "View"}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-40 rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black ring-opacity-5 py-1">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                opt.value === value ? "bg-brand/5 text-brand" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared table sub-components
// ---------------------------------------------------------------------------

function ProcessorChip({ processor }: { processor: string }) {
  return (
    <span className="inline-block rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      {processor}
    </span>
  );
}

function BillingTableHead({ entityCols }: { entityCols: string[] }) {
  const { taxLabel } = useLobRegion();
  return (
    <thead>
      <tr className="text-xs font-medium text-gray-400">
        {entityCols.map((col) => (
          <th key={col} className="border-b border-gray-100 bg-gray-50 px-6 py-3 text-left text-gray-500" rowSpan={2}>
            {col}
          </th>
        ))}
        <th className="border-b border-gray-100 bg-blue-50 px-6 py-3 text-center uppercase tracking-wide text-blue-500" colSpan={4}>
          Issuance
        </th>
        <th className="border-b border-gray-100 bg-purple-50 px-6 py-3 text-center uppercase tracking-wide text-purple-500" colSpan={3}>
          Redemption
        </th>
        <th className="border-b border-gray-100 bg-gray-50 px-6 py-3 text-right" rowSpan={2}>
          Grand Total
        </th>
      </tr>
      <tr className="text-xs font-medium text-gray-400">
        <th className="border-b border-gray-100 bg-blue-50 px-6 py-2.5 text-right">#</th>
        <th className="border-b border-gray-100 bg-blue-50 px-6 py-2.5 text-right">Amt</th>
        <th className="border-b border-gray-100 bg-blue-50 px-6 py-2.5 text-right">Fee</th>
        <th className="border-b border-gray-100 bg-blue-50 px-6 py-2.5 text-right">{taxLabel}</th>
        <th className="border-b border-gray-100 bg-purple-50 px-6 py-2.5 text-right">#</th>
        <th className="border-b border-gray-100 bg-purple-50 px-6 py-2.5 text-right">Fee</th>
        <th className="border-b border-gray-100 bg-purple-50 px-6 py-2.5 text-right">{taxLabel}</th>
      </tr>
    </thead>
  );
}

function BillingDataCells({ row }: { row: any }) {
  const { fmt } = useBillingFormat();
  return (
    <>
      <td className="px-6 py-4 text-right tabular-nums text-gray-700">{(row.issuanceCount ?? 0).toLocaleString()}</td>
      <td className="px-6 py-4 text-right tabular-nums text-gray-700">{fmt(row.issuanceAmount)}</td>
      <td className="px-6 py-4 text-right tabular-nums text-gray-700">{fmt(row.issuanceFee)}</td>
      <td className="px-6 py-4 text-right tabular-nums text-gray-400">{fmt(row.issuanceGst)}</td>
      <td className="px-6 py-4 text-right tabular-nums text-gray-700">{(row.redemptionCount ?? 0).toLocaleString()}</td>
      <td className="px-6 py-4 text-right tabular-nums text-gray-700">{fmt(row.redemptionFee)}</td>
      <td className="px-6 py-4 text-right tabular-nums text-gray-400">{fmt(row.redemptionGst)}</td>
      <td className="px-6 py-4 text-right">
        <span className="font-bold text-gray-900">{fmt(row.grandTotal)}</span>
      </td>
    </>
  );
}

function BillingTotalsRow({ rows, colSpan }: { rows: any[]; colSpan: number }) {
  const { fmt } = useBillingFormat();
  if (rows.length <= 1) return null;
  const t = sumRows(rows);
  return (
    <tfoot>
      <tr className="bg-gray-50 border-t-2 border-gray-200 text-sm font-semibold text-gray-700">
        <td className="px-6 py-3" colSpan={colSpan}>Total</td>
        <td className="px-6 py-3 text-right tabular-nums">{t.issuanceCount.toLocaleString()}</td>
        <td className="px-6 py-3 text-right tabular-nums">{fmt(t.issuanceAmount)}</td>
        <td className="px-6 py-3 text-right tabular-nums">{fmt(t.issuanceFee)}</td>
        <td className="px-6 py-3 text-right tabular-nums text-gray-400">{fmt(t.issuanceGst)}</td>
        <td className="px-6 py-3 text-right tabular-nums">{t.redemptionCount.toLocaleString()}</td>
        <td className="px-6 py-3 text-right tabular-nums">{fmt(t.redemptionFee)}</td>
        <td className="px-6 py-3 text-right tabular-nums text-gray-400">{fmt(t.redemptionGst)}</td>
        <td className="px-6 py-3 text-right font-bold text-gray-900">{fmt(t.grandTotal)}</td>
      </tr>
    </tfoot>
  );
}

// ---------------------------------------------------------------------------
// VoucherTable — uses individual primitive props to avoid stale closure issues
// ---------------------------------------------------------------------------

function VoucherTable({
  billingMonth, apiBase, processorPrefix, programId, principalId,
}: {
  billingMonth: string;
  apiBase: ApiBase;
  processorPrefix?: string;
  programId?: number;
  principalId?: number;
}) {
  const { taxLabel } = useLobRegion();
  const { fmt, fmtDate } = useBillingFormat();
  const [data, setData] = useState<VoucherDetailResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    const body: Record<string, unknown> = { billingMonth, page: p, limit: 50 };
    if (processorPrefix !== undefined) body.processorPrefix = processorPrefix;
    if (programId    !== undefined)    body.programId    = programId;
    if (principalId  !== undefined)    body.principalId  = principalId;
    try {
      const res = await makeApiClient(apiBase).post("/voucher-detail", body);
      setData(res.data);
      setPage(p);
    } catch { setData(null); }
    finally  { setLoading(false); }
  }, [billingMonth, apiBase, processorPrefix, programId, principalId]);

  useEffect(() => { load(1); }, [load]);

  const totalPages = data ? Math.ceil(data.total / 50) : 1;

  return (
    <div>
      <div className="px-6 py-3 border-b border-gray-100">
        <span className="text-xs text-gray-400">{data ? `${data.total.toLocaleString()} vouchers` : ""}</span>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-brand" />
        </div>
      )}
      {!loading && data?.total === 0 && (
        <p className="py-12 text-center text-sm text-gray-400">No vouchers found for this period.</p>
      )}

      {!loading && data && data.total > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide">
                  <th className="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-gray-400" colSpan={2}>Voucher</th>
                  <th className="border-b border-gray-100 bg-blue-50 px-3 py-2.5 text-center text-blue-500" colSpan={3}>Issuance</th>
                  <th className="border-b border-gray-100 bg-purple-50 px-3 py-2.5 text-center text-purple-500" colSpan={4}>Redemption</th>
                  <th className="border-b border-gray-100 bg-gray-50 px-3 py-2.5 text-right text-gray-400">Grand Total</th>
                </tr>
                <tr className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  <th className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-left">Saral ID</th>
                  <th className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-right">Amt</th>
                  <th className="border-b border-gray-100 bg-blue-50 px-3 py-2 text-right text-blue-400">Fee</th>
                  <th className="border-b border-gray-100 bg-blue-50 px-3 py-2 text-right text-blue-400">{taxLabel}</th>
                  <th className="border-b border-gray-100 bg-blue-50 px-3 py-2 text-right text-blue-400">Total</th>
                  <th className="border-b border-gray-100 bg-purple-50 px-3 py-2 text-center text-purple-400">#</th>
                  <th className="border-b border-gray-100 bg-purple-50 px-3 py-2 text-right text-purple-400">Fee</th>
                  <th className="border-b border-gray-100 bg-purple-50 px-3 py-2 text-right text-purple-400">{taxLabel}</th>
                  <th className="border-b border-gray-100 bg-purple-50 px-3 py-2 text-right text-purple-400">Total</th>
                  <th className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.data.map((row: VoucherDetailRow) => (
                  <tr key={row.voucherId} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 bg-gray-50/30">
                      <div className="font-mono text-xs text-gray-500 whitespace-nowrap">{row.saralId || "—"}</div>
                      <div className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">{fmtDate(row.voucherCreatedAt)}</div>
                    </td>
                    <td className="px-3 py-3 text-right text-gray-700 bg-gray-50/30 whitespace-nowrap">{fmt(row.voucherAmount)}</td>
                    <td className="px-3 py-3 text-right text-gray-700 bg-blue-50/30 whitespace-nowrap">
                      {row.issuanceTotal > 0 ? fmt(row.issuanceFee) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-400 bg-blue-50/30 whitespace-nowrap">
                      {row.issuanceTotal > 0 ? fmt(row.issuanceGst) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900 bg-blue-50/30 whitespace-nowrap">
                      {row.issuanceTotal > 0 ? fmt(row.issuanceTotal) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-center bg-purple-50/30">
                      {row.redemptionCount > 0
                        ? <span className="font-semibold text-purple-700">{row.redemptionCount}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-700 bg-purple-50/30 whitespace-nowrap">
                      {row.redemptionCount > 0 ? fmt(row.redemptionFee) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-400 bg-purple-50/30 whitespace-nowrap">
                      {row.redemptionCount > 0 ? fmt(row.redemptionGst) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900 bg-purple-50/30 whitespace-nowrap">
                      {row.redemptionCount > 0 ? fmt(row.redemptionTotal) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right font-bold text-gray-900 whitespace-nowrap">{fmt(row.grandTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3">
              <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => load(page - 1)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Previous</button>
                <button disabled={page >= totalPages} onClick={() => load(page + 1)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DrillDownPanel — shared; accepts pre-computed flat filter values
// ---------------------------------------------------------------------------

function DrillDownPanel({
  label, processor, billingMonth, apiBase, onClose,
  processorPrefix, programId, principalId,
}: {
  label: string;
  processor?: string;
  billingMonth: string;
  apiBase: ApiBase;
  onClose: () => void;
  processorPrefix?: string;
  programId?: number;
  principalId?: number;
}) {
  return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10">
            <span className="text-xs font-bold text-brand">{label.slice(0, 2).toUpperCase()}</span>
          </div>
          <span className="text-sm font-semibold text-gray-900">{label}</span>
          {processor && <ProcessorChip processor={processor} />}
          <span className="text-sm text-gray-400">{fmtMonth(billingMonth)}</span>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
      <VoucherTable
        key={`${processorPrefix ?? ""}-${programId ?? ""}-${principalId ?? ""}-${billingMonth}`}
        billingMonth={billingMonth}
        apiBase={apiBase}
        processorPrefix={processorPrefix}
        programId={programId}
        principalId={principalId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payables tab (cost side — billing-mis)
// ---------------------------------------------------------------------------

const PAYABLES_OPTIONS = [
  { value: "processor", label: "Processor" },
  { value: "program",   label: "Program"   },
  { value: "principal", label: "Principal" },
];

function PayablesTab({ billingMonth }: { billingMonth: string }) {
  const dispatch = useAppDispatch();
  const { processorSummary, programSummary, principalSummary, loading, error } =
    useAppSelector((s: RootState) => s.billingMIS);

  const [viewMode,       setViewMode]       = useState<PayablesViewMode>("processor");
  const [selectedEntity, setSelectedEntity] = useState<PayablesSelectedEntity | null>(null);
  const [isExporting,    setIsExporting]    = useState(false);

  useEffect(() => {
    setSelectedEntity(null);
    if (viewMode === "processor") dispatch(fetchProcessorSummary(billingMonth));
    if (viewMode === "program")   dispatch(fetchProgramSummary(billingMonth));
    if (viewMode === "principal") dispatch(fetchPrincipalSummary(billingMonth));
  }, [dispatch, billingMonth, viewMode]);

  const currentData =
    viewMode === "processor" ? processorSummary?.data :
    viewMode === "program"   ? programSummary?.data   :
                               principalSummary?.data;

  const entityColumns =
    viewMode === "processor" ? ["Processor"]
    : viewMode === "program"  ? ["Program", "Processor"]
    :                           ["Principal", "Processor"];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const body: Record<string, unknown> = { billingMonth, viewMode };
      if (selectedEntity) {
        if (selectedEntity.kind === "processor") body.processorPrefix = selectedEntity.processorPrefix;
        else if (selectedEntity.kind === "program") body.programId = selectedEntity.programId;
        else { body.principalId = selectedEntity.principalId; body.processorPrefix = selectedEntity.processor; }
      }
      const res = await makeApiClient("billing-mis").post("/export", body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `billing-mis-${selectedEntity ? selectedEntity.label.replace(/\s+/g, "-") : viewMode}-${billingMonth}.xlsx`);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { alert("Export failed."); }
    finally  { setIsExporting(false); }
  };

  const rowClass = (sel: boolean) =>
    `cursor-pointer transition-colors ${sel ? "bg-brand/5 ring-1 ring-inset ring-brand/20" : "hover:bg-gray-50/80"}`;

  const dot = (sel: boolean) => sel
    ? <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
    : null;

  const renderBody = () => {
    if (viewMode === "processor" && processorSummary) {
      return processorSummary.data.map((row: ProcessorRow) => {
        const sel = selectedEntity?.kind === "processor" && selectedEntity.processorPrefix === row.processor;
        const entity: PayablesSelectedEntity = { kind: "processor", label: row.processor, processorPrefix: row.processor };
        return (
          <tr key={row.processor} onClick={() => setSelectedEntity(sel ? null : entity)} className={rowClass(sel)}>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2.5">{dot(sel)}<span className="font-semibold text-gray-900">{row.processor}</span></div>
            </td>
            <BillingDataCells row={row} />
          </tr>
        );
      });
    }
    if (viewMode === "program" && programSummary) {
      return programSummary.data.map((row: ProgramRow) => {
        const sel = selectedEntity?.kind === "program" && selectedEntity.programId === row.programId;
        const entity: PayablesSelectedEntity = { kind: "program", label: row.programName || `Program ${row.programId}`, programId: row.programId, processor: row.processor };
        return (
          <tr key={`${row.programId}-${row.processor}`} onClick={() => setSelectedEntity(sel ? null : entity)} className={rowClass(sel)}>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2.5">{dot(sel)}<span className="font-medium text-gray-900">{row.programName || "—"}</span></div>
            </td>
            <td className="px-6 py-4"><ProcessorChip processor={row.processor} /></td>
            <BillingDataCells row={row} />
          </tr>
        );
      });
    }
    if (viewMode === "principal" && principalSummary) {
      return principalSummary.data.map((row: PrincipalRow) => {
        const sel = selectedEntity?.kind === "principal" && selectedEntity.principalId === row.principalId && selectedEntity.processor === row.processor;
        const entity: PayablesSelectedEntity = { kind: "principal", label: row.principalName || `Principal ${row.principalId}`, principalId: row.principalId, processor: row.processor };
        return (
          <tr key={`${row.principalId}-${row.processor}`} onClick={() => setSelectedEntity(sel ? null : entity)} className={rowClass(sel)}>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2.5">{dot(sel)}<span className="font-medium text-gray-900">{row.principalName || "—"}</span></div>
            </td>
            <td className="px-6 py-4"><ProcessorChip processor={row.processor} /></td>
            <BillingDataCells row={row} />
          </tr>
        );
      });
    }
    return null;
  };

  // Flatten entity into primitive props for DrillDownPanel
  const drillProps = selectedEntity ? (() => {
    if (selectedEntity.kind === "processor") return {
      label: selectedEntity.label,
      processor: undefined as string | undefined,
      processorPrefix: selectedEntity.processorPrefix,
      programId: undefined as number | undefined,
      principalId: undefined as number | undefined,
    };
    if (selectedEntity.kind === "program") return {
      label: selectedEntity.label,
      processor: selectedEntity.processor,
      processorPrefix: selectedEntity.processor,
      programId: selectedEntity.programId,
      principalId: undefined as number | undefined,
    };
    return {
      label: selectedEntity.label,
      processor: selectedEntity.processor,
      processorPrefix: selectedEntity.processor,
      programId: undefined as number | undefined,
      principalId: selectedEntity.principalId,
    };
  })() : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <ViewModeSelect value={viewMode} onChange={(v) => setViewMode(v as PayablesViewMode)} options={PAYABLES_OPTIONS} />
        <button
          onClick={handleExport}
          disabled={isExporting || !currentData?.length}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-sm hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <Download className="h-4 w-4" />
          {isExporting ? "Exporting…" : "Download MIS"}
        </button>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
        {loading && (
          <div className="flex justify-center py-16">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-brand" />
          </div>
        )}
        {error && !loading && (
          <p className="py-12 text-center text-sm text-red-500">{error}</p>
        )}
        {!loading && currentData && currentData.length === 0 && (
          <p className="py-16 text-center text-sm text-gray-400">No payables found for {fmtMonth(billingMonth)}.</p>
        )}
        {!loading && currentData && currentData.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <BillingTableHead entityCols={entityColumns} />
              <tbody className="divide-y divide-gray-50">{renderBody()}</tbody>
              <BillingTotalsRow rows={currentData} colSpan={entityColumns.length} />
            </table>
          </div>
        )}
      </div>

      {drillProps && (
        <DrillDownPanel
          key={`pay-${selectedEntity!.kind}-${drillProps.label}-${billingMonth}`}
          billingMonth={billingMonth}
          apiBase="billing-mis"
          onClose={() => setSelectedEntity(null)}
          {...drillProps}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Receivables tab (revenue side — revenue-mis)
// ---------------------------------------------------------------------------

const RECEIVABLES_OPTIONS = [
  { value: "principal", label: "Principal" },
  { value: "program",   label: "Program"   },
];

function ReceivablesTab({ billingMonth }: { billingMonth: string }) {
  const [viewMode,       setViewMode]       = useState<ReceivablesViewMode>("principal");
  const [selectedEntity, setSelectedEntity] = useState<ReceivablesSelectedEntity | null>(null);
  const [isExporting,    setIsExporting]    = useState(false);

  const [principalData, setPrincipalData] = useState<SummaryResult<RevenuePrincipalRow> | null>(null);
  const [programData,   setProgramData]   = useState<SummaryResult<RevenueProgramRow>   | null>(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  useEffect(() => {
    setSelectedEntity(null);
    setError(null);
    setLoading(true);
    const endpoint = viewMode === "principal" ? "/summary" : "/program-summary";
    makeApiClient("revenue-mis").post(endpoint, { billingMonth })
      .then((res) => {
        if (viewMode === "principal") setPrincipalData(res.data);
        else setProgramData(res.data);
      })
      .catch(() => setError("Failed to load receivables data."))
      .finally(() => setLoading(false));
  }, [billingMonth, viewMode]);

  const currentData = viewMode === "principal" ? principalData?.data : programData?.data;
  const entityColumns = viewMode === "principal" ? ["Principal", "Processor"] : ["Program", "Principal", "Processor"];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const body: Record<string, unknown> = { billingMonth, viewMode };
      if (selectedEntity) {
        if (selectedEntity.kind === "principal") {
          body.principalId     = selectedEntity.principalId;
          body.processorPrefix = selectedEntity.processor;
        } else {
          body.programId       = selectedEntity.programId;
          body.principalId     = selectedEntity.principalId;
          body.processorPrefix = selectedEntity.processor;
        }
      }
      const res = await makeApiClient("revenue-mis").post("/export", body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `revenue-mis-${selectedEntity ? selectedEntity.label.replace(/\s+/g, "-") : viewMode}-${billingMonth}.xlsx`);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { alert("Export failed."); }
    finally  { setIsExporting(false); }
  };

  const rowClass = (sel: boolean) =>
    `cursor-pointer transition-colors ${sel ? "bg-brand/5 ring-1 ring-inset ring-brand/20" : "hover:bg-gray-50/80"}`;

  const dot = (sel: boolean) => sel
    ? <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
    : null;

  const renderBody = () => {
    if (viewMode === "principal" && principalData) {
      return principalData.data.map((row) => {
        const sel = selectedEntity?.kind === "principal" && selectedEntity.principalId === row.principalId && selectedEntity.processor === row.processor;
        const entity: ReceivablesSelectedEntity = { kind: "principal", label: row.principalName || `Principal ${row.principalId}`, principalId: row.principalId, processor: row.processor };
        return (
          <tr key={`${row.principalId}-${row.processor}`} onClick={() => setSelectedEntity(sel ? null : entity)} className={rowClass(sel)}>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2.5">{dot(sel)}<span className="font-medium text-gray-900">{row.principalName || "—"}</span></div>
            </td>
            <td className="px-6 py-4"><ProcessorChip processor={row.processor} /></td>
            <BillingDataCells row={row} />
          </tr>
        );
      });
    }
    if (viewMode === "program" && programData) {
      return programData.data.map((row) => {
        const sel = selectedEntity?.kind === "program" && selectedEntity.programId === row.programId && selectedEntity.processor === row.processor;
        const entity: ReceivablesSelectedEntity = { kind: "program", label: row.programName || `Program ${row.programId}`, programId: row.programId, principalId: row.principalId, processor: row.processor };
        return (
          <tr key={`${row.programId}-${row.processor}`} onClick={() => setSelectedEntity(sel ? null : entity)} className={rowClass(sel)}>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2.5">{dot(sel)}<span className="font-medium text-gray-900">{row.programName || "—"}</span></div>
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">{row.principalName || "—"}</td>
            <td className="px-6 py-4"><ProcessorChip processor={row.processor} /></td>
            <BillingDataCells row={row} />
          </tr>
        );
      });
    }
    return null;
  };

  const drillProps = selectedEntity ? (() => {
    if (selectedEntity.kind === "principal") return {
      label: selectedEntity.label,
      processor: selectedEntity.processor,
      processorPrefix: selectedEntity.processor,
      programId: undefined as number | undefined,
      principalId: selectedEntity.principalId,
    };
    return {
      label: selectedEntity.label,
      processor: selectedEntity.processor,
      processorPrefix: selectedEntity.processor,
      programId: selectedEntity.programId,
      principalId: selectedEntity.principalId,
    };
  })() : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <ViewModeSelect value={viewMode} onChange={(v) => setViewMode(v as ReceivablesViewMode)} options={RECEIVABLES_OPTIONS} />
        <button
          onClick={handleExport}
          disabled={isExporting || !currentData?.length}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-sm hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <Download className="h-4 w-4" />
          {isExporting ? "Exporting…" : "Download MIS"}
        </button>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
        {loading && (
          <div className="flex justify-center py-16">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-brand" />
          </div>
        )}
        {error && !loading && (
          <p className="py-12 text-center text-sm text-red-500">{error}</p>
        )}
        {!loading && !error && currentData && currentData.length === 0 && (
          <p className="py-16 text-center text-sm text-gray-400">No receivables found for {fmtMonth(billingMonth)}.</p>
        )}
        {!loading && !error && currentData && currentData.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <BillingTableHead entityCols={entityColumns} />
              <tbody className="divide-y divide-gray-50">{renderBody()}</tbody>
              <BillingTotalsRow rows={currentData} colSpan={entityColumns.length} />
            </table>
          </div>
        )}
      </div>

      {drillProps && (
        <DrillDownPanel
          key={`rec-${selectedEntity!.kind}-${drillProps.label}-${billingMonth}`}
          billingMonth={billingMonth}
          apiBase="revenue-mis"
          onClose={() => setSelectedEntity(null)}
          {...drillProps}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main: BillingInvoicing
// ---------------------------------------------------------------------------

const BillingInvoicing: React.FC = () => {
  const [billingMonth, setBillingMonth] = useState<string>(getCurrentMonth);
  const [activeTab,    setActiveTab]    = useState<ActiveTab>("payables");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-5 flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold text-gray-900 shrink-0">Billing &amp; Invoicing</h1>

          <div className="flex items-center gap-3">
            {/* Premium pill-style tab switcher */}
            <div className="inline-flex items-center rounded-full bg-gray-100 p-1 gap-0.5">
              <button
                onClick={() => setActiveTab("payables")}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeTab === "payables"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Payables
              </button>
              <button
                onClick={() => setActiveTab("receivables")}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeTab === "receivables"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Receivables
              </button>
            </div>

            <div className="h-5 w-px bg-gray-200" />

            <MonthPicker value={billingMonth} onChange={setBillingMonth} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 sm:px-8 py-8">
        {activeTab === "payables"
          ? <PayablesTab key="payables" billingMonth={billingMonth} />
          : <ReceivablesTab key="receivables" billingMonth={billingMonth} />
        }
      </div>
    </div>
  );
};

export default BillingInvoicing;
